/**
 * LegendItem Component
 *
 * Renders one layer's legend from the normalized LayerLegend model. Each entry
 * is an image (WMS / static), a colour swatch (circle/line/fill), or a derived
 * FontAwesome icon (symbol/live layers, including fire Active/Out). This is what
 * lets geojson/live layers appear in the legend — not just WMS.
 */

import React, { useState } from 'react';
import { faIconToSvg } from '../../layers2/utils/iconUtils';
import { LayerLegend, LegendSymbol } from './legendModel';

interface LegendItemProps {
  legend: LayerLegend;
}

/** Display height (px) for a derived icon swatch, scaled by its symbology size. */
function iconHeightPx(size: number): number {
  return Math.min(28, Math.max(12, Math.round(24 * size)));
}

function svgDataUri(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const SymbolView: React.FC<{ symbol: LegendSymbol; alt: string }> = ({ symbol, alt }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  if (symbol.kind === 'swatch') {
    const base: React.CSSProperties = { backgroundColor: symbol.color };
    const style: React.CSSProperties =
      symbol.shape === 'circle' ? { ...base, borderRadius: '50%' }
      : symbol.shape === 'line' ? { ...base, height: 3, alignSelf: 'center' }
      : base;
    return <span className={`legend-item__swatch legend-item__swatch--${symbol.shape}`} style={style} />;
  }

  if (symbol.kind === 'icon') {
    const svg = faIconToSvg(symbol.iconName, { color: symbol.color, size: 1 });
    if (!svg) {
      // Icon glyph unavailable — show a colour swatch so the entry isn't blank.
      return <span className="legend-item__swatch" style={{ backgroundColor: symbol.color }} />;
    }
    return (
      <img
        src={svgDataUri(svg)}
        alt={alt}
        className="legend-item__icon"
        style={{ height: iconHeightPx(symbol.size) }}
      />
    );
  }

  if (symbol.kind === 'images') {
    const h = Math.round(40 * symbol.size);
    return (
      <span className="legend-item__images" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        {symbol.urls.map((url, i) => (
          <img key={i} src={url} alt={alt} className="legend-item__image legend-item__image--loaded" style={{ height: h, width: 'auto' }} />
        ))}
      </span>
    );
  }

  // image
  if (imageError) {
    return <span className="legend-item__error-text">Legend unavailable</span>;
  }
  return (
    <img
      src={symbol.url}
      alt={alt}
      className={`legend-item__image ${imageLoaded ? 'legend-item__image--loaded' : ''}`}
      onLoad={() => setImageLoaded(true)}
      onError={() => setImageError(true)}
    />
  );
};

const LegendItem: React.FC<LegendItemProps> = ({ legend }) => {
  return (
    <div className="legend-item">
      <div className="legend-item__header">
        <span className="legend-item__name">{legend.layerName}</span>
      </div>
      <div className="legend-item__content">
        {legend.entries.map((entry, index) => (
          <div key={index} className="legend-item__swatch-row">
            <SymbolView symbol={entry.symbol} alt={`${legend.layerName} ${entry.label}`.trim()} />
            {entry.label && <span className="legend-item__swatch-label">{entry.label}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LegendItem;
